import { useCallback, useEffect, useRef, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import DOMPurify from "dompurify"

import trainCss from "@/data/weekly-train.css?raw"
import { train, type TrainDoc } from "@/api/train"
import { applySlots, computeSlots, type TrainConfig, weekMondays } from "@/lib/trainGen"
import {
  addBar,
  addItem,
  applyBar,
  completionTarget,
  type CompletionLog,
  dayColumns,
  injectAssignees,
  injectCheckboxes,
  injectEditChrome,
  parseAssignees,
  parseBar,
  propagateWeekToFuture,
  removeEditChrome,
  serialize,
  setAssignees,
  type BarProps,
} from "@/lib/trainDom"
import { BarModal, type BarModalState } from "@/components/train/BarModal"
import { AssigneePicker, type AssignState } from "@/components/train/AssigneePicker"
import { SaveDialog } from "@/components/train/SaveDialog"
import { LoginDialog } from "@/components/golive/dialogs"
import { golive } from "@/api/golive"

const DEFAULT_CONFIG: TrainConfig = {
  anchorMonday: "2026-08-17",
  anchorGa: "1.10.0",
  cadenceWeeks: 1,
}

/** Editing is open (anonymous) on a public URL, so the stored HTML is untrusted
 *  even in an isolated Shadow DOM (which blocks <script> but not `onerror=`).
 *  Sanitize on both save and render; keep style/class/data-* so the matrix grid
 *  placement and the done/hidden flags survive. */
const clean = (html: string): string => DOMPurify.sanitize(html, { ADD_ATTR: ["style"] })

/** Text-bearing leaves that are inline-editable. Bars are edited via the modal
 *  (not inline) and day/date headers are generator-managed, so both are omitted. */
const EDITABLE = [
  ".masthead .eyebrow",
  ".masthead h1",
  ".fact dt",
  ".fact dd",
  "section > h2",
  "section > .sub",
  ".calhead",
  ".wklabel .wknum",
  ".wklabel .wkspan",
  ".day .date",
  ".day .train",
  ".day .phase",
  ".day li",
  ".mx-week",
  ".lane-name",
  ".lane-env",
  ".rule h3",
  ".rule p",
  ".foot span",
].join(",")

function fmtWhen(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

/** The CAT-OS Weekly Release Train — a shared, editable reference page rendered
 *  in an isolated Shadow DOM (original CSS kept verbatim). Version/date chips
 *  auto-advance to the current week; calendar items are a live task list; matrix
 *  bars are edited through a modal. */
export function WeeklyTrain() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["train"],
    queryFn: train.get,
    staleTime: 5 * 60 * 1000,
  })

  const hostRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<ShadowRoot | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [cfg, setCfg] = useState<TrainConfig>(DEFAULT_CONFIG)
  const [modal, setModal] = useState<BarModalState | null>(null)
  const [assign, setAssign] = useState<AssignState | null>(null)

  // Team roster for the assignee picker (same list as Go-Live).
  const { data: roster } = useQuery({
    queryKey: ["golive-people"],
    queryFn: golive.people,
    staleTime: 60 * 60 * 1000,
  })

  // Editor session (shared with Go-Live). When editing requires a code and none
  // is held, edit actions prompt for it first.
  const [session, setSession] = useState<{ authRequired: boolean; user: string | null }>({
    authRequired: false,
    user: null,
  })
  const [promptOpen, setPromptOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const pendingRef = useRef<(() => void) | null>(null)
  const needsUnlockRef = useRef(false)
  useEffect(() => {
    golive.whoami().then(setSession).catch(() => {})
  }, [])
  useEffect(() => {
    needsUnlockRef.current = session.authRequired && !session.user
  }, [session])

  /** Run `action` now if a session is held (or none is needed); otherwise open
   *  the code prompt and run it after a successful unlock. */
  const withSession = (action: () => void) => {
    if (!needsUnlockRef.current) return action()
    pendingRef.current = action
    setPromptOpen(true)
  }

  // Latest-value refs so the once-attached DOM listeners never go stale.
  const editingRef = useRef(false)
  const cfgRef = useRef(cfg)
  const mondaysRef = useRef<string[]>(weekMondays(DEFAULT_CONFIG))
  const logRef = useRef<CompletionLog>({})
  const setCompletionRef = useRef<(li: Element, done: boolean) => void>(() => {})
  const openBarRef = useRef<(el: Element, isNew: boolean) => void>(() => {})
  const openAssignRef = useRef<(li: Element) => void>(() => {})
  const autosaveRef = useRef<() => void>(() => {})
  const promptRef = useRef<() => void>(() => {})
  useEffect(() => {
    editingRef.current = editing
  }, [editing])
  useEffect(() => {
    cfgRef.current = cfg
    mondaysRef.current = weekMondays(cfg)
  }, [cfg])
  useEffect(() => {
    if (data && !editing) setCfg(data.config ?? DEFAULT_CONFIG)
  }, [data, editing])

  const saveNow = useCallback(
    async (html: string, config: TrainConfig) => {
      const updated = await train.save(html, config)
      queryClient.setQueryData<TrainDoc>(["train"], updated)
      return updated
    },
    [queryClient],
  )

  // Attach the shadow root + delegated listeners once.
  useEffect(() => {
    if (!hostRef.current || shadowRef.current) return
    const shadow = hostRef.current.attachShadow({ mode: "open" })
    const style = document.createElement("style")
    style.textContent = trainCss
    const content = document.createElement("div")
    shadow.append(style, content)
    shadowRef.current = shadow
    contentRef.current = content

    content.addEventListener("click", (e) => {
      const t = e.target as HTMLElement
      const c = contentRef.current
      if (!c) return
      if (t.closest(".tr-x")) {
        if (!editingRef.current) return
        t.closest("li")?.remove()
        return
      }
      if (t.closest(".tr-add-item")) {
        if (!editingRef.current) return
        const day = t.closest(".day")
        if (day) {
          addItem(day)
          injectCheckboxes(c, mondaysRef.current, logRef.current)
          injectEditChrome(c)
          injectAssignees(c, true)
          applyEditable(c, true)
        }
        return
      }
      if (t.closest(".tr-assign") || (editingRef.current && t.closest(".tr-assignees"))) {
        if (!editingRef.current) return
        const li = t.closest("li")
        if (li) openAssignRef.current(li)
        return
      }
      if (t.closest(".tr-add-bar")) {
        if (!editingRef.current) return
        const lane = t.closest(".lane")
        if (lane) openBarRef.current(addBar(c, lane), true)
        return
      }
      const bar = t.closest(".bar")
      if (bar && editingRef.current) openBarRef.current(bar, false)
    })

    content.addEventListener("change", (e) => {
      const t = e.target as HTMLElement
      if (!t.classList.contains("li-chk")) return
      const cb = t as HTMLInputElement
      const li = cb.closest("li")
      if (!li || !contentRef.current) return
      // Toggling a checkbox is an edit — needs a code if editing is gated.
      if (needsUnlockRef.current) {
        cb.checked = !cb.checked // revert the visual; the user can retry after unlocking
        promptRef.current()
        return
      }
      // Optimistically reflect the tick, then persist just this one change through
      // the merge endpoint — recorded against the calendar week this task's row is
      // showing, so it never repeats into future weeks or clobbers a concurrent edit.
      li.classList.toggle("done", cb.checked)
      setCompletionRef.current(li, cb.checked)
    })
  }, [])

  // One effect owns the shadow content. In view mode it (re)paints from the
  // stored doc, fills today's version/date chips, and adds the task-list
  // checkboxes. In edit mode it only layers on the edit affordances — it never
  // repaints, so in-progress edits are never clobbered. Keeping both branches in
  // a single effect avoids any ordering/stale-closure window between them.
  useEffect(() => {
    const c = contentRef.current
    if (!c || !data) return
    if (editing) {
      applyEditable(c, true)
      injectEditChrome(c)
      injectAssignees(c, true)
      return
    }
    applyEditable(c, false) // authoritative: clear any lingering edit state/class
    c.innerHTML = clean(data.html)
    const activeCfg = data.config ?? DEFAULT_CONFIG
    applySlots(c, computeSlots(activeCfg))
    mondaysRef.current = weekMondays(activeCfg)
    logRef.current = data.completions ?? {}
    injectCheckboxes(c, mondaysRef.current, logRef.current)
    injectAssignees(c, false)
  }, [data, editing])

  const handleEdit = () =>
    withSession(() => {
      setMsg(null)
      setEditing(true)
    })

  const handleCancel = () => {
    const c = contentRef.current
    if (c) {
      removeEditChrome(c)
      applyEditable(c, false)
    }
    setCfg(data?.config ?? DEFAULT_CONFIG)
    setEditing(false) // triggers a repaint from stored data
    setMsg(null)
  }

  const doSave = async (applyToFuture: boolean) => {
    const c = contentRef.current
    if (!c) return
    setSaving(true)
    setMsg(null)
    removeEditChrome(c)
    applyEditable(c, false)
    let html = serialize(c, clean)
    // "Apply to future" syncs the current week's task/assignee edits forward into
    // the recurring template (the next-week row), so upcoming weeks match.
    if (applyToFuture) html = clean(propagateWeekToFuture(html))
    try {
      await saveNow(html, cfg)
      setSaveOpen(false)
      setEditing(false)
      setMsg(applyToFuture ? "Saved & applied to future weeks." : "Saved.")
      window.setTimeout(() => setMsg(null), 2500)
    } catch (e) {
      const err = e as Error & { status?: number }
      if (err.status === 401) {
        setSaveOpen(false)
        setSession((s) => ({ ...s, user: null }))
        setPromptOpen(true)
        setMsg("Enter your access code to save.")
      } else {
        setMsg(`Save failed: ${err.message}`)
      }
      applyEditable(c, true)
      injectEditChrome(c)
    } finally {
      setSaving(false)
    }
  }

  const handleRegenerate = () => {
    const c = contentRef.current
    if (!c) return
    const n = applySlots(c, computeSlots(cfg))
    setMsg(`Regenerated ${n} version/date fields across all three weeks — review, then Save.`)
  }

  // Keep the once-bound listeners pointed at fresh callbacks.
  openBarRef.current = (el, isNew) =>
    setModal({ el, props: parseBar(el), columns: dayColumns(contentRef.current!), isNew })
  openAssignRef.current = (li) => setAssign({ li, current: parseAssignees(li) })
  promptRef.current = () => setPromptOpen(true)
  setCompletionRef.current = (li, done) => {
    const target = completionTarget(li, mondaysRef.current)
    if (!target) return
    train
      .setCompletion(target.week, target.task, done)
      .then((updated) => {
        // Server-merged truth (includes other editors' concurrent checks).
        logRef.current = updated.completions ?? {}
        queryClient.setQueryData<TrainDoc>(["train"], updated)
      })
      .catch((e) => {
        // Roll back the optimistic tick and surface the reason.
        const cb = li.querySelector<HTMLInputElement>(":scope > .li-chk")
        if (cb) cb.checked = !done
        li.classList.toggle("done", !done)
        if ((e as { status?: number }).status === 401) {
          setSession((s) => ({ ...s, user: null }))
          setPromptOpen(true)
          setMsg("Enter your access code to update the checklist.")
        } else {
          setMsg("Could not save that check — try again.")
        }
      })
  }
  autosaveRef.current = () => {
    const c = contentRef.current
    if (!c) return
    saveNow(serialize(c, clean), cfgRef.current).catch((e) => {
      if ((e as { status?: number }).status === 401) {
        setSession((s) => ({ ...s, user: null }))
        setPromptOpen(true)
      }
      setMsg("Save failed.")
    })
  }

  const applyBarFromModal = (p: BarProps) => {
    // Sanitize the modal's rich text before it re-enters the document.
    if (modal) applyBar(modal.el, { ...p, text: clean(p.text) })
    setModal(null)
  }
  const deleteBarFromModal = () => {
    modal?.el.remove()
    setModal(null)
  }
  const cancelModal = () => {
    if (modal?.isNew) modal.el.remove()
    setModal(null)
  }

  const applyAssignees = (names: string[]) => {
    if (assign && contentRef.current) {
      setAssignees(assign.li, names)
      injectAssignees(contentRef.current, true) // re-render the chips in place
    }
    setAssign(null)
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          {data?.updated_at ? (
            <span>
              Last edited {fmtWhen(data.updated_at)}
              {data.updated_by && data.updated_by !== "anonymous" ? ` by ${data.updated_by}` : ""}
            </span>
          ) : (
            <span>Editable reference — changes are shared with everyone.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {msg && (
            <span
              className={
                /^(Saved|Regenerated)/.test(msg) ? "text-xs text-emerald-600" : "text-xs text-amber-700"
              }
            >
              {msg}
            </span>
          )}
          {!editing ? (
            <button
              onClick={handleEdit}
              className="inline-flex items-center gap-1.5 rounded-lg bg-header px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-ink"
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setSaveOpen(true)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Anchor Monday (ref)
              <input
                type="date"
                value={cfg.anchorMonday}
                onChange={(e) => setCfg({ ...cfg, anchorMonday: e.target.value })}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              GA on anchor (V0)
              <input
                type="text"
                value={cfg.anchorGa}
                onChange={(e) => setCfg({ ...cfg, anchorGa: e.target.value })}
                placeholder="1.10.0"
                className="w-24 rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Cadence
              <select
                value={cfg.cadenceWeeks}
                onChange={(e) => setCfg({ ...cfg, cadenceWeeks: Number(e.target.value) })}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-normal normal-case tracking-normal text-slate-700"
              >
                <option value={1}>Weekly</option>
                <option value={2}>Every 2 weeks</option>
              </select>
            </label>
            <button
              onClick={handleRegenerate}
              className="inline-flex items-center gap-1.5 rounded-lg border border-header/30 bg-white px-3 py-1.5 text-xs font-semibold text-header shadow-sm transition hover:bg-header/5"
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Preview current dates
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            The calendar shows three weeks &mdash; <b>previous, current and next</b> &mdash; and the
            versions &amp; dates <b>advance automatically</b> from this anchor so the middle week is
            always the current one. In
            the calendar, <b>+ item</b>/<b>×</b> add or remove tasks and the checkboxes mark them done.
            In the Streams matrix, <b>click a bar</b> to edit its text, day span, colour or visibility,
            or <b>+ bar</b> to add one. Click any highlighted text to edit it; the grid layout is fixed.
          </p>
        </div>
      )}

      {isLoading && <p className="p-6 text-sm text-slate-500">Loading…</p>}
      {isError && <p className="p-6 text-sm text-red-600">Failed to load: {(error as Error)?.message}</p>}

      <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white">
        <div ref={hostRef} />
      </div>

      {modal && (
        <BarModal
          state={modal}
          onApply={applyBarFromModal}
          onCancel={cancelModal}
          onDelete={deleteBarFromModal}
        />
      )}

      {assign && (
        <AssigneePicker
          state={assign}
          roster={roster ?? []}
          onApply={applyAssignees}
          onCancel={() => setAssign(null)}
        />
      )}

      {saveOpen && <SaveDialog busy={saving} onCancel={() => setSaveOpen(false)} onSave={doSave} />}

      <LoginDialog
        open={promptOpen}
        onClose={() => {
          pendingRef.current = null
          setPromptOpen(false)
        }}
        onLoggedIn={(name) => {
          setSession({ authRequired: true, user: name })
          setPromptOpen(false)
          const pending = pendingRef.current
          pendingRef.current = null
          pending?.()
        }}
      />
    </div>
  )
}

/** Toggle inline editability: EDITABLE leaves become editable; version/date
 *  placeholders stay locked (generator-managed). */
function applyEditable(content: HTMLElement, on: boolean) {
  const root = content.getRootNode()
  const host = root instanceof ShadowRoot ? (root.host as HTMLElement) : null
  if (on) {
    content.querySelectorAll(EDITABLE).forEach((el) => el.setAttribute("contenteditable", "true"))
    // Version/date placeholders stay locked (generator-managed).
    content.querySelectorAll("[data-ph]").forEach((el) => el.setAttribute("contenteditable", "false"))
    host?.classList.add("editing")
  } else {
    content.querySelectorAll("[contenteditable]").forEach((el) => {
      // Keep the checkbox islands non-editable; clear editing off everything else.
      if (!el.classList.contains("li-chk")) el.removeAttribute("contenteditable")
    })
    host?.classList.remove("editing")
  }
}
